import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useQuoteListOrgId } from 'src/api/helpers/getOrgId'
import { useSupabaseClient } from 'src/api/supabase'
import { DELIVERED_STATUS } from 'src/shared/consts/quoteStatus'
import { useToast } from 'src/shared/hooks/use-toast'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { convertFromSEK, fromEURtoSEK } from 'src/shared/utils/currency'
import {
  APPROVED_CHOICE_FIELDS,
  isBeforeCustomerApproval,
  isLockedApprovedChoice,
  planAfterDelete,
} from 'src/shared/utils/quoteAlternatives'
import { Database } from 'supabase/database.types'

import { trackEvent } from './apiAnalytics'
import {
  AlternativesSaveError,
  writeAlternativeUpdates,
} from './apiQuoteAlternatives'
import { queryKeys } from './queryKeys'

/**
 * Staff tried to change or delete the customer's approved option, or to delete
 * one of the alternatives the customer chose between (`alternative`).
 */
export class ApprovedChoiceLockedError extends Error {
  constructor(readonly alternative = false) {
    super('The customer approved this option; it is locked')
    this.name = 'ApprovedChoiceLockedError'
  }
}

export type InsertQuoteItem =
  Database['public']['Tables']['quote_item']['Insert']
export type UpdateQuoteItem =
  Database['public']['Tables']['quote_item']['Update']

export type QuoteItem_ = NonNullable<
  ReturnType<typeof useQuoteItemApi>['quoteItems']['data']
>[number]

/** Alias for quote item with relations - used in FinalizeOrder */
export type QuoteItemByQuote = QuoteItem_

function convertQuoteItemRow(item: any, currency: string) {
  const productPriceConverted = convertFromSEK(item.product?.price, currency)
  let unitPriceConverted = convertFromSEK(item.unit_price, currency)
  if (
    item.type === 'product' &&
    item.product &&
    productPriceConverted != null &&
    (unitPriceConverted == null ||
      (typeof unitPriceConverted === 'number' &&
        typeof productPriceConverted === 'number' &&
        unitPriceConverted < productPriceConverted * 0.9))
  ) {
    unitPriceConverted = productPriceConverted
  }
  const converted = {
    ...item,
    unit_price: unitPriceConverted,
    product: {
      ...item.product,
      price: productPriceConverted,
    },
    quote_sub_item: item.quote_sub_item?.map((sub: any) => ({
      ...sub,
      unit_price: convertFromSEK(sub.unit_price, currency),
    })),
  }

  return converted
}

export const useQuoteItemApi = ({
  quote_item_id,
  quote_id,
  displayCurrency,
}: {
  quote_item_id?: number
  quote_id?: number
  /**
   * Currency to show fetched prices in. Defaults to the viewer's organization
   * currency; the public proposal passes the quote's own (quoted) currency.
   * Only affects reads — writes keep converting from the viewer's currency.
   */
  displayCurrency?: string
}) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { toast } = useToast()
  const { t } = useLocalization()
  const currency = useOrganizationCurrency()
  const readCurrency = displayCurrency ?? currency

  const createQuoteItem = useMutation({
    mutationFn: async (insertQuoteItem: InsertQuoteItem) => {
      const payload = { ...insertQuoteItem }
      if (payload.unit_price != null && currency === 'EUR') {
        payload.unit_price =
          fromEURtoSEK(payload.unit_price) ?? payload.unit_price
      }
      return await supabase
        .from('quote_item')
        .insert(payload)
        .select('id')
        .single()
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      if (variables.quote_id) {
        trackEvent(supabase, {
          name: 'item_change',
          quoteId: variables.quote_id,
        })
      }

      return data
    },
  })

  /**
   * Throws when the row is the customer's accepted line on an approved quote.
   * With `forDelete`, also when it is any alternative on such a quote: all
   * alternatives are kept as the record of what the customer chose between.
   */
  const assertNotApprovedChoice = async (id: number, forDelete = false) => {
    const { data, error } = await supabase
      .from('quote_item')
      .select('quote_id, slot, customer_selected_at, quote!inner(status)')
      .eq('id', id)
      .maybeSingle()
    // Fail closed: without the row we cannot tell whether it is locked.
    if (error) throw error
    if (!data) return
    if (isLockedApprovedChoice(data)) {
      throw new ApprovedChoiceLockedError()
    }
    if (!forDelete || !data.slot?.trim() || data.quote_id == null) return
    if (!isBeforeCustomerApproval(data.quote?.status)) {
      throw new ApprovedChoiceLockedError(true)
    }
    // Reopened after approval: the recorded choice still keeps its options.
    const { data: chosen, error: chosenError } = await supabase
      .from('quote_item')
      .select('id')
      .eq('quote_id', data.quote_id)
      .not('customer_selected_at', 'is', null)
      .limit(1)
    if (chosenError) throw chosenError
    if (chosen?.length) throw new ApprovedChoiceLockedError(true)
  }

  const lockedToast = (error: unknown) => {
    if (!(error instanceof ApprovedChoiceLockedError)) return false
    toast({
      variant: 'destructive',
      title: t('This line is approved', 'Raden är godkänd'),
      description: error.alternative
        ? t(
            'The customer chose between these options, so they are kept with the order.',
            'Kunden valde mellan de här alternativen, så de sparas med ordern.'
          )
        : t(
            'The customer approved this option, so its product, price and quantity can no longer be changed.',
            'Kunden har godkänt det här alternativet, så produkt, pris och antal kan inte längre ändras.'
          ),
    })
    return true
  }

  const updateQuoteItem = useMutation({
    mutationFn: async (updateQuoteItem: UpdateQuoteItem) => {
      // Callers send the line's own quote_id along with e.g. notes; that alone
      // is not a change, so the lock looks at quote_id only when it differs.
      const touchesApproval = APPROVED_CHOICE_FIELDS.some(
        (field) => field !== 'quote_id' && field in updateQuoteItem
      )
      if (updateQuoteItem.id && touchesApproval) {
        await assertNotApprovedChoice(updateQuoteItem.id)
      } else if (updateQuoteItem.id && updateQuoteItem.quote_id != null) {
        const { data, error } = await supabase
          .from('quote_item')
          .select('quote_id')
          .eq('id', updateQuoteItem.id)
          .maybeSingle()
        if (error) throw error
        if (data && data.quote_id !== updateQuoteItem.quote_id) {
          await assertNotApprovedChoice(updateQuoteItem.id)
        }
      }

      const payload = { ...updateQuoteItem }

      if (updateQuoteItem.unit_price != null && currency === 'EUR') {
        payload.unit_price =
          fromEURtoSEK(updateQuoteItem.unit_price) ?? updateQuoteItem.unit_price
      }

      const quote_item = await supabase
        .from('quote_item')
        .update(payload)
        .eq('id', updateQuoteItem.id!)
        .select('id')
        .single()

      if ('quantity' in updateQuoteItem) {
        const quote_sub_items = await supabase
          .from('quote_sub_item')
          .select('id, type')
          .in('type', ['decoration'])
          .eq('quote_item_id', updateQuoteItem.id!)

        const decoration_ids = quote_sub_items.data?.map((qsi) => qsi.id)

        if (decoration_ids?.length) {
          await supabase
            .from('quote_sub_item')
            .update({
              quantity: updateQuoteItem.quantity,
            })
            .in('id', decoration_ids)
        }
      }
      return quote_item
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })

      toast({
        title: t('Updated', 'Uppdaterat'),
        variant: 'success',
      })
    },
    onError: (error) => {
      lockedToast(error)
    },
  })

  const deleteQuoteItem = useMutation({
    mutationFn: async (deleteQuoteItem: { id: number; quote_id: number }) => {
      await assertNotApprovedChoice(deleteQuoteItem.id, true)

      const { data: deleted, error } = await supabase
        .from('quote_item')
        .delete()
        .eq('id', deleteQuoteItem.id)
        .eq('quote_id', deleteQuoteItem.quote_id!)
        .select('id, slot')
      if (error) throw error
      if (!deleted?.length) {
        // The row may be deletable but not readable back; only fail when it
        // demonstrably still exists.
        const { data: still, error: stillError } = await supabase
          .from('quote_item')
          .select('id')
          .eq('id', deleteQuoteItem.id)
        if (stillError) throw stillError
        if (still?.length) {
          throw new Error(`quote_item ${deleteQuoteItem.id} was not deleted`)
        }
        return
      }

      // A deleted alternative leaves its slot behind: settle what is left so a
      // last line becomes an ordinary line again (the pick is never moved).
      const slot = deleted[0].slot?.trim()
      if (slot) {
        const { data: rest, error: restError } = await supabase
          .from('quote_item')
          .select('id, slot, is_recommended')
          .eq('quote_id', deleteQuoteItem.quote_id!)
          .not('slot', 'is', null)
        if (restError) throw restError
        const remaining = (rest ?? []).filter(
          (line) => line.slot?.trim() === slot
        )
        await writeAlternativeUpdates(
          supabase,
          planAfterDelete(remaining),
          remaining
        )
      }
    },
    onError: (error) => {
      if (lockedToast(error)) return
      console.error('[quote_item] delete failed', error)
      toast({
        variant: 'destructive',
        title: t('Could not delete the item', 'Kunde inte radera produkten'),
        description:
          error instanceof AlternativesSaveError
            ? t(
                'The item was deleted, but its alternatives could not be tidied up. Refresh and check them.',
                'Produkten raderades, men dess alternativ kunde inte uppdateras. Ladda om och kontrollera dem.'
              )
            : t('Please try again.', 'Försök igen.'),
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
      trackEvent(supabase, {
        name: 'item_change',
        quoteId: variables.quote_id,
      })

      toast({
        title: t('Item deleted', 'Produkt raderad'),
        variant: 'success',
      })
    },
  })

  const shared_query = `
  id,
  quote_id,
  product_id,
  product_variant_color_id,
  quantity,
  sizes,
  type,
  notes,
  created_at,
  unit_price,
  discount,
  description,
  slot,
  is_recommended,
  customer_selected_at,

  quote!inner(org_id),

  quote_sub_item!quote_sub_item_quote_item_id_fkey(*, design!design_quote_sub_item_id_fkey(*)),

  product:product_open!quote_item_product_id_fkey(
  id,
  item_name,
  item_description,
  source,
  moq,
  price,
  print_codes,
  article_number,
  klayder_recommends,
  klayder_recommendation_text,
  related_product_ids,

  category_product(category(title)),

  product_variant(*,
      product_variant_color(*,
        product_image(*)))
  
    )` as const

  const quoteItem = useQuery({
    queryKey: [queryKeys.quoteItemById, quote_item_id, readCurrency],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quote_item')
        .select(shared_query)
        .eq('id', quote_item_id!)
        .single()

      if (error) throw error
      return convertQuoteItemRow(data, readCurrency)
    },
    enabled: !!quote_item_id,
  })

  const quoteItems = useQuery({
    queryKey: [queryKeys.quoteItems, quote_id, readCurrency],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quote_item')
        .select(shared_query)
        .eq('quote_id', quote_id!)

      if (error) throw error
      return (data ?? []).map((row) => convertQuoteItemRow(row, readCurrency))
    },
    enabled: !!quote_id,
  })

  return {
    createQuoteItem,
    updateQuoteItem,
    deleteQuoteItem,
    quoteItem,
    quoteItems,
  }
}

const deliveredHistoryProductRefsSelect = `
  id,
  product_id,
  product_variant_color_id,
  created_at,
  quote!inner(org_id, status)
` as const

export type DeliveredHistoryProductRefRow = {
  id: number
  product_id: number | null
  product_variant_color_id: number | null
  created_at: string | null
}

export const useDeliveredQuoteItemsForHistory = () => {
  const supabase = useSupabaseClient()
  const quoteListOrgId = useQuoteListOrgId()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [
      queryKeys.quoteItems,
      'delivered-history-product-refs',
      quoteListOrgId,
    ],
    queryFn: async () => {
      let query = supabase
        .from('quote_item')
        .select(deliveredHistoryProductRefsSelect)
        .eq('quote.status', DELIVERED_STATUS)
        .eq('type', 'product')
        .order('created_at', { ascending: false })

      if (quoteListOrgId) {
        query = query.eq('quote.org_id', quoteListOrgId)
      }

      const { data, error } = await query

      if (error) throw error
      return (data ?? []) as DeliveredHistoryProductRefRow[]
    },
    enabled: !!quoteListOrgId || isAdmin,
  })
}
